<template>
	<el-container class="group-page">
		<el-aside class="page-aside" v-show="!configStore.isMobile || !mobileShowDetail">
			<div class="header" :class="configStore.electronMode ? 'header-menu-wrap' : ''">
				<el-input class="search-text im-session-search" size="small" placeholder="搜索群聊名称" v-model="searchText">
					<i class="el-icon-search el-input__icon" slot="prefix"> </i>
				</el-input>
				<el-button v-if="canCreateGroup" plain class="add-btn im-header-icon-btn" icon="el-icon-plus" title="创建群聊"
					@click="onCreateGroup"></el-button>
			</div>
			<virtual-scroller
				ref="groupScroller"
				class="group-items"
				:items="groupRows"
				:item-height-getter="getGroupRowHeight"
				:buffer="8"
				@reach-bottom="onGroupListReachBottom"
			>
				<template v-slot="{ item }">
					<div v-if="item.type === 'letter'" class="letter">{{ item.letter }}</div>
					<div v-else-if="item.type === 'group'">
						<group-item
							:id="item.group.id"
							:group="item.group"
							:active="item.group.id == activeGroupId"
							@chat="onSendMessage(item.group)"
							@quit="onQuit(item.group)"
							@dissolve="onDissolve(item.group)"
							@card="onSendCard(item.group)"
							@click.native="onActiveItem(item.group)"
						></group-item>
					</div>
					<div v-else class="divider"></div>
				</template>
			</virtual-scroller>
		</el-aside>
		<div class="group-main-area" v-show="!configStore.isMobile || mobileShowDetail">
			<chat-box v-if="activeGroupChat" :chat="activeGroupChat" @back="onMobileBack"></chat-box>
			<empty-state
				v-else
				class="content-empty-state"
				icon="el-icon-chat-dot-round"
				title="选择一个群组开始聊天"
				hint="从左侧列表中选择群组，群资料和成员管理可在会话侧栏继续操作"
			></empty-state>
		</div>
		<chat-selector ref="chatSel" title="分享名片"></chat-selector>
		<quick-create-group
			:dialogVisible="showQuickCreateGroup"
			@close="showQuickCreateGroup = false"
		></quick-create-group>
	</el-container>
</template>

<script>
import { pinyin } from 'pinyin-pro';
import ChatSelector from '../components/chat/ChatSelector.vue';
import ChatBox from '../components/chat/ChatBox.vue';
import GroupItem from '../components/group/GroupItem';
import QuickCreateGroup from '../components/group/QuickCreateGroup.vue';
import VirtualScroller from '../components/common/VirtualScroller.vue';
import { resolveGroupChatDisplayName } from '../utils/identityDisplay.js';
import { preloadChatRouteView } from '../router/lazyViews.js';
import {
	extractForwardErrorMessage,
	normalizeForwardMessages,
	prepareForwardTargets
} from '../utils/messageForward.js';

export default {
	name: "group",
	components: {
		ChatSelector,
		ChatBox,
		GroupItem,
		QuickCreateGroup,
		VirtualScroller
	},
	data() {
		return {
			searchText: "",
			activeGroupId: null,
			mobileShowDetail: false,
			showQuickCreateGroup: false,
			loadingMore: false
		};
	},
	computed: {
		mine() {
			return this.userStore.userInfo;
		},
		canCreateGroup() {
			return this.$permission.canCreateGroup();
		},
		activeGroup() {
			return this.groupStore.findGroup(this.activeGroupId) || {};
		},
		activeGroupChat() {
			if (!this.activeGroupId) {
				return null;
			}
			return this.chatStore.findChatByGroup(this.activeGroupId);
		},
			groupMembers() {
				const group = this.groupStore.findGroup(this.activeGroupId);
				return group ? group.members : [];
			},
			visibleGroups() {
				const groups = [];
				const seen = new Set();
				this.groupStore.groups.forEach((group) => {
					const groupKey = group && group.id !== undefined && group.id !== null ? String(group.id) : '';
					if (!groupKey || seen.has(groupKey)) {
						return;
					}
					const showGroupName = resolveGroupChatDisplayName(group) || '';
					if (group.quit || (this.searchText && !showGroupName.includes(this.searchText))) {
						return;
					}
					seen.add(groupKey);
					groups.push(group);
				});
				return groups;
			},
			shouldKeepServerOrder() {
				const page = this.groupStore.list && this.groupStore.list.page ? this.groupStore.list.page : {};
				return !this.searchText && (!page.loaded || page.loading || page.more);
			},
			groupMap() {
				let map = new Map();
				this.visibleGroups.forEach((group) => {
					const showGroupName = resolveGroupChatDisplayName(group) || '';
					let letter = this.firstLetter(showGroupName).toUpperCase();
					if (!this.isEnglish(letter)) {
						letter = "#";
					}
				if (map.has(letter)) {
					map.get(letter).push(group);
				} else {
					map.set(letter, [group]);
				}
			});
			map.forEach((groups) => {
				groups.sort((left, right) => {
				const leftName = resolveGroupChatDisplayName(left) || left.name || '';
				const rightName = resolveGroupChatDisplayName(right) || right.name || '';
				const nameCompare = leftName.localeCompare(rightName, 'zh-Hans', {
					numeric: true,
					sensitivity: 'base'
				});
				if (nameCompare !== 0) {
					return nameCompare;
				}
				return String(left && left.id || '').localeCompare(String(right && right.id || ''), undefined, {
					numeric: true
					});
				});
			});
			const arrayObj = Array.from(map);
			arrayObj.sort((a, b) => {
				if (a[0] == '#' || b[0] == '#') {
					return b[0].localeCompare(a[0]);
				}
				return a[0].localeCompare(b[0]);
			});
			return new Map(arrayObj.map(item => [item[0], item[1]]));
		},
		groupKeys() {
			return Array.from(this.groupMap.keys());
		},
		groupValues() {
			return Array.from(this.groupMap.values());
			},
			groupRows() {
				if (this.shouldKeepServerOrder) {
					return this.visibleGroups.map(group => ({
						type: 'group',
						key: `group-${group.id}`,
						group
					}));
				}
				const rows = [];
				this.groupValues.forEach((groups, index) => {
				const letter = this.groupKeys[index];
				rows.push({
					type: 'letter',
					key: `letter-${letter}`,
					letter
				});
				groups.forEach(group => {
					rows.push({
						type: 'group',
						key: `group-${group.id}`,
						group
					});
				});
				if (index < this.groupValues.length - 1) {
					rows.push({
						type: 'divider',
						key: `divider-${letter}`
					});
				}
			});
			return rows;
		}
	},
	methods: {
		getGroupRowHeight(item) {
			if (!item) {
				return 60;
			}
			if (item.type === 'letter') {
				return 42;
			}
			if (item.type === 'divider') {
				return 13;
			}
			return 72;
		},
		buildGroupChat(group) {
			return {
				type: 'GROUP',
				targetId: group.id,
				showName: resolveGroupChatDisplayName(group) || group.name,
				headImage: group.headImageThumb
			};
		},
		ensureGroupChat(group) {
			const chatInfo = this.buildGroupChat(group);
			this.chatStore.openChat(chatInfo);
			return this.chatStore.findChatByGroup(group.id);
		},
		scheduleChatRoutePreload() {
			const run = () => {
				preloadChatRouteView().catch((error) => {
					console.warn('预加载会话页面失败:', error);
				});
			};
			if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
				window.requestIdleCallback(run, { timeout: 1200 });
				return;
			}
			setTimeout(run, 300);
		},
		onCreateGroup() {
			if (!this.canCreateGroup) {
				this.$message.error("您没有创建群组的权限");
				return;
			}
			this.scheduleChatRoutePreload();
			this.showQuickCreateGroup = true;
		},
		onActiveItem(group) {
			if (!group || group.quit) {
				return;
			}
			this.activeGroupId = group.id;
			this.mobileShowDetail = true;
			this.ensureGroupChat(group);
		},
		handleEscape() {
			if (this.showQuickCreateGroup) {
				this.showQuickCreateGroup = false;
				return true;
			}
			if (this.$refs.chatSel && this.$refs.chatSel.show) {
				this.$refs.chatSel.close();
				return true;
			}
			if (this.activeGroupId) {
				this.closeDetailPanel();
				return true;
			}
			return false;
		},
		onMobileBack() {
			this.mobileShowDetail = false;
		},
		closeDetailPanel() {
			this.activeGroupId = null;
			this.mobileShowDetail = false;
		},
		async sendForwardMessages(chats, messages, options = {}) {
			const forwardTarget = prepareForwardTargets(chats);
			const targets = forwardTarget.targets;
			const forwardMessages = normalizeForwardMessages(messages);
			if (targets.length === 0) {
				this.$message.warning('请选择转发目标');
				return null;
			}
			if (forwardMessages.length === 0) {
				this.$message.warning('没有可发送的消息');
				return null;
			}
			if (forwardTarget.truncated) {
				this.$message.warning(`已选择 ${forwardTarget.originalCount} 个会话，本次仅转发前 ${forwardTarget.limit} 个会话`);
			}
			try {
				return await this.$http({
					url: '/message/forward',
					method: 'post',
					data: {
						targets,
						messages: forwardMessages
					},
					_skipErrorMessage: true
				});
			} catch (error) {
				this.$message.error(extractForwardErrorMessage(error) || options.failText || '发送失败');
				return null;
			}
		},
		async onSendCard(group) {
			if (!this.$permission.canForwardCardMessage()) {
				this.$message.error('您没有名片转发权限');
				return;
			}
			this.$refs.chatSel.open(async (chats) => {
				try {
					const token = await this.$http({
						url: '/group/card/token/' + group.id,
						method: 'get',
						_skipErrorMessage: true
					});
					const cardData = {
						groupId: group.id,
						groupName: group.name,
						headImage: group.headImageThumb,
						token: token
					};
					await this.sendForwardMessages(chats, [{
						type: this.$enums.MESSAGE_TYPE.GROUP_CARD,
						content: JSON.stringify(cardData)
					}], {
						successText: '发送成功',
						failText: '发送失败'
					});
				} catch (error) {
					this.$message.error(extractForwardErrorMessage(error) || '发送失败');
				}
			});
		},
		onDissolve(group) {
			this.$confirm(`确认要解散'${group.name}'吗?`, '确认解散?', {
				confirmButtonText: '确定',
				cancelButtonText: '取消',
				type: 'warning'
			}).then(() => {
				this.$http({
					url: `/group/delete/${group.id}`,
					method: 'delete'
				}).then(() => {
					this.$message.success(`群聊'${group.name}'已解散`);
					this.groupStore.removeGroup(group.id);
					this.reset(group.id);
				});
			});
		},
		onQuit(group) {
			const groupDisplayName = resolveGroupChatDisplayName(group) || group.name || '群聊';
			this.$confirm(`确认退出'${groupDisplayName}',并清空聊天记录吗？`, '确认退出?', {
				confirmButtonText: '确定',
				cancelButtonText: '取消',
				type: 'warning'
			}).then(() => {
				this.$http({
					url: `/group/quit/${group.id}`,
					method: 'delete'
				}).then(() => {
					this.$message.success(`您已退出'${groupDisplayName}'`);
					this.groupStore.removeGroup(group.id);
					this.chatStore.removeGroupChat(group.id);
					this.reset(group.id);
				});
			});
		},
		onSendMessage(group) {
			const chat = this.buildGroupChat(group);
			this.chatStore.openChat(chat);
			this.chatStore.setActiveChat(chat);
			this.$router.push("/home/chat");
		},
		reloadMembers() {
			if (!this.activeGroupId) {
				return;
			}
			this.groupStore.refreshMember(this.activeGroupId, {
				refreshOnline: false,
				pageLimit: 30
			});
		},
		reset(groupId) {
			if (this.activeGroupId && this.activeGroupId == groupId) {
				this.activeGroupId = null;
				this.mobileShowDetail = false;
			}
		},
		locateItem(id) {
			const element = document.getElementById(id);
			if (element) {
				element.scrollIntoView({ behavior: 'smooth' });
			}
		},
		firstLetter(text) {
			if (!text) {
				return "#";
			}
			const pyText = pinyin(text, {
				toneType: 'none',
				type: 'normal'
			});
			return pyText[0] || "#";
		},
		isEnglish(character) {
			return /^[A-Za-z]+$/.test(character);
		},
		onGroupListReachBottom() {
			if (this.searchText) {
				return;
			}
			if (this.loadingMore || !this.groupStore.list.page.more || !this.groupStore.list.page.cursor) {
				return;
			}
			this.loadingMore = true;
			this.groupStore.loadMoreGroupsFromCursor({
				maxPages: 1
			}).catch((error) => {
				console.warn('滚动续拉群分页失败', error);
			}).finally(() => {
				this.loadingMore = false;
			});
		},
		getGroupScrollerMetrics() {
			const groupScroller = this.$refs.groupScroller;
			const container = groupScroller && groupScroller.$refs ? groupScroller.$refs.container : null;
			if (!container) {
				return null;
			}
			return {
				scrollHeight: container.scrollHeight,
				clientHeight: container.clientHeight
			};
		},
		maybeAutoLoadGroupList() {
			if (this.searchText) {
				return;
			}
			if (this.loadingMore || this.groupStore.list.page.loading) {
				return;
			}
			if (!this.groupStore.list.page.more || !this.groupStore.list.page.cursor) {
				return;
			}
			this.$nextTick(() => {
				const metrics = this.getGroupScrollerMetrics();
				if (!metrics) {
					return;
				}
				if (metrics.scrollHeight > metrics.clientHeight + 4) {
					return;
				}
				this.onGroupListReachBottom();
			});
		}
	},
	watch: {
		'groupRows.length'() {
			this.maybeAutoLoadGroupList();
		},
		searchText() {
			this.maybeAutoLoadGroupList();
		}
	},
	mounted() {
		this.scheduleChatRoutePreload();
		this.maybeAutoLoadGroupList();
		const groupId = Number(this.$route.query.id || 0);
		if (!groupId) {
			return;
		}
		const group = this.groupStore.findGroup(groupId);
		if (!group || group.quit) {
			return;
		}
		this.onActiveItem(group);
		this.locateItem(group.id);
	}
}
</script>

<style lang="scss" scoped>
@import "../assets/style/responsive";

.group-page {
	.page-aside {
		width: clamp(280px, 28vw, 312px);
		min-width: 0;
		display: flex;
		flex-direction: column;
		flex-shrink: 0;
		background: var(--im-session-bg);
		background-image: none;
		border-right: 1px solid var(--im-surface-panel-border);

		@include mobile {
			width: 100%;
			flex: 1 1 auto;
			border-right: none;
		}
	}

	.group-main-area {
		flex: 1;
		display: flex;
		min-width: 0;
		overflow: hidden;
		background: var(--im-chat-bg);

		> .chat-box {
			flex: 1;
			min-width: 0;
		}
	}

	.header {
		height: 64px;
		display: flex;
		align-items: center;
		padding: 0 12px;
		background: var(--im-session-bg);
		background-image: none;
		gap: 8px;

		.search-text {
			flex: 1;
			min-width: 0;
		}
	}

	.group-items {
		flex: 1;
		background: var(--im-session-bg);
		background-image: none;
	}

	.letter {
		padding: 16px 16px 8px;
		font-size: 13px;
		font-weight: 700;
		color: var(--im-session-text);
		letter-spacing: 0.02em;
		text-align: left;
	}

	.divider {
		margin: 6px 16px;
		border-bottom: 1px solid rgba(var(--im-black-rgb), 0.06);
	}

	.content-empty-state {
		background: var(--im-chat-bg);
	}
}
</style>
